/**
 * Guest account client (handegg-accounts-spec.md §3): bootstraps a guest on first launch (`POST /auth/guest`),
 * keeps the device secret + rotating refresh token in the host's secret store (expo-secure-store / Keychain on
 * native; falls back to plain KeyValueStorage and migrates existing guests), the 15 min access JWT in memory,
 * and recovers with `/auth/refresh` → `/auth/device`. Soft failures everywhere so a down server never bricks a build.
 *
 * Platform-free (client-core boundary): the host injects `fetch`, storage, and optional async `secretStorage`.
 */
import type { KeyValueStorage } from '../platform';

export interface AccountFetchResponse {
  readonly status: number;
  json(): Promise<unknown>;
}
export type AccountFetch = (
  url: string,
  init: { method: string; headers: Record<string, string>; body?: string },
) => Promise<AccountFetchResponse>;

/** Async secret store (native: expo-secure-store). Sync KeyValueStorage remains the migration / web fallback. */
export interface SecretStorage {
  getItem(key: string): Promise<string | null>;
  setItem(key: string, value: string): Promise<void>;
  deleteItem?(key: string): Promise<void>;
}

export interface AccountClientOptions {
  /** HTTP origin of the account API, e.g. https://handegg-server.handegg.workers.dev (no trailing slash). */
  readonly baseUrl: string;
  readonly storage: KeyValueStorage | null;
  /** Preferred home for the account blob (device secret). When set, plain `storage` is only used to migrate. */
  readonly secretStorage?: SecretStorage | null;
  readonly fetch: AccountFetch;
  /** `ios` | `android` | `web` (devices.platform). */
  readonly platform: string;
  readonly now?: () => number;
  readonly timeoutMs?: number;
  readonly setTimeout?: (fn: () => void, ms: number) => unknown;
}

interface Persisted {
  userId: string;
  deviceId: string;
  deviceSecret: string;
  refreshToken: string | null;
}

interface TokenResponse {
  user_id: string;
  device_id: string;
  access_token: string;
  access_expires_at: number;
  refresh_token: string;
  device_secret?: string;
}

export interface ApiResult<T = Record<string, unknown>> {
  readonly status: number;
  readonly body: T | null;
}

export interface LinkAppleResult {
  readonly status: number;
  readonly body: (TokenResponse & Record<string, unknown>) | null;
}

const KEY = 'hf.account.v1';
/**
 * Link calls verify a third-party token server-side (JWKS fetch on a cold isolate + Apple code exchange):
 * the default 8 s cut them off and reported 'offline' while the server was still succeeding.
 */
const LINK_TIMEOUT_MS = 25_000;

/** Refresh the access token this long before it expires. */
const EARLY_MS = 60_000;

/** `wss://host/ws` → `https://host` (and ws → http): the account API sits on the lobby's Worker. */
export const accountBaseFromWsUrl = (wsUrl: string): string | null => {
  const m = /^(wss?):\/\/([^/?#]+)/.exec(wsUrl.trim());
  if (!m) return null;
  return `${m[1] === 'wss' ? 'https' : 'http'}://${m[2]}`;
};

const isTokenResponse = (v: unknown): v is TokenResponse => {
  const o = v as Partial<TokenResponse> | null;
  return (
    !!o &&
    typeof o.user_id === 'string' &&
    typeof o.device_id === 'string' &&
    typeof o.access_token === 'string'
  );
};

const parsePersisted = (raw: string | null): Persisted | null => {
  if (!raw) return null;
  try {
    const p = JSON.parse(raw) as Partial<Persisted>;
    if (typeof p.userId !== 'string' || typeof p.deviceId !== 'string' || typeof p.deviceSecret !== 'string')
      return null;
    return {
      userId: p.userId,
      deviceId: p.deviceId,
      deviceSecret: p.deviceSecret,
      refreshToken: p.refreshToken ?? null,
    };
  } catch {
    return null;
  }
};

export class AccountClient {
  private saved: Persisted | null = null;
  private access: { token: string; exp: number } | null = null;
  private inflight: Promise<boolean> | null = null;
  private hydrateGate: Promise<void> | null = null;
  private hydrated = false;
  private readonly now: () => number;

  constructor(private readonly o: AccountClientOptions) {
    this.now = o.now ?? (() => Date.now());
    // Sync path when there is no async secret store (tests / web without SecureStore).
    if (!o.secretStorage) this.saved = parsePersisted(o.storage?.getItem(KEY) ?? null);
  }

  /** The account's user id once bootstrapped (persisted across launches), else null. */
  get userId(): string | null {
    return this.saved?.userId ?? null;
  }

  /**
   * The current access token for the WebSocket URL, possibly a little stale (the server allows sockets a grace
   * period); null before the first successful bootstrap of this launch. Kicks a background refresh when due.
   */
  accessToken(): string | null {
    if (this.access && this.access.exp - EARLY_MS < this.now()) void this.ensure();
    return this.access?.token ?? null;
  }

  /** Make sure a fresh access token exists (bootstrap / refresh / device login / new guest). False when offline. */
  ensure(): Promise<boolean> {
    if (this.access && this.access.exp - EARLY_MS > this.now()) return Promise.resolve(true);
    if (!this.inflight) {
      this.inflight = this.bootstrap()
        .catch(() => false)
        .then((ok) => {
          this.inflight = null;
          return ok;
        });
    }
    return this.inflight;
  }

  /** Authenticated JSON call; one retry after a 401 with a renewed token. Network failure → status 0. */
  async call<T = Record<string, unknown>>(
    method: string,
    path: string,
    body?: unknown,
  ): Promise<ApiResult<T>> {
    if (!(await this.ensure())) return { status: 0, body: null };
    let r = await this.raw<T>(method, path, body, this.currentToken());
    if (r.status === 401) {
      this.access = null;
      if (!(await this.ensure())) return { status: 0, body: null };
      r = await this.raw<T>(method, path, body, this.currentToken());
    }
    return r;
  }

  /**
   * Link Sign in with Apple (`POST /auth/link/apple`). On success adopts any returned tokens (same guest, or
   * switched to the Apple account). 409 `merge_blocked` is returned as-is for Amir.
   */
  async linkApple(input: {
    idToken: string;
    nonce?: string;
    authorizationCode?: string | null;
    /** Apple returns the user's name only on the first authorization: forwarded so the server can keep it. */
    fullName?: string | null;
  }): Promise<LinkAppleResult> {
    if (!(await this.ensure())) return { status: 0, body: null };
    const r = await this.raw<TokenResponse & Record<string, unknown>>(
      'POST',
      '/auth/link/apple',
      {
        id_token: input.idToken,
        ...(input.nonce ? { nonce: input.nonce } : {}),
        ...(input.authorizationCode ? { authorization_code: input.authorizationCode } : {}),
        ...(input.fullName ? { full_name: input.fullName } : {}),
      },
      this.currentToken(),
      LINK_TIMEOUT_MS,
    );
    if (r.status === 200 && r.body) this.adopt(r.body);
    return r;
  }

  /** POST /auth/link/google — link (or sign in to) the account owning this Google ID token. */
  async linkGoogle(input: { idToken: string }): Promise<LinkAppleResult> {
    if (!(await this.ensure())) return { status: 0, body: null };
    const r = await this.raw<TokenResponse & Record<string, unknown>>(
      'POST',
      '/auth/link/google',
      { id_token: input.idToken },
      this.currentToken(),
      LINK_TIMEOUT_MS,
    );
    if (r.status === 200 && r.body) this.adopt(r.body);
    return r;
  }

  /**
   * POST /auth/email/start — email a single-use 15 min sign-in link to `email` (server trims + lowercases).
   * 200 `{ ok: true }` for any valid address; 400 `invalid_email`; 429 `rate_limited`; 0 offline.
   */
  async startEmailLink(input: { email: string }): Promise<ApiResult<{ ok?: boolean; error?: string }>> {
    return this.call<{ ok?: boolean; error?: string }>('POST', '/auth/email/start', {
      email: input.email.trim(),
    });
  }

  /**
   * POST /auth/email/verify — redeem the token from `handegg://auth/email?token=…`. On success adopts the returned
   * tokens (same account, or switched to the account that owns the address) like linkApple. 401 `link_used` /
   * `link_expired` / `invalid_link`; 409 `merge_blocked`.
   */
  async verifyEmailLink(input: { token: string }): Promise<LinkAppleResult> {
    if (!(await this.ensure())) return { status: 0, body: null };
    const r = await this.raw<TokenResponse & Record<string, unknown>>(
      'POST',
      '/auth/email/verify',
      { token: input.token },
      this.currentToken(),
    );
    if (r.status === 200 && r.body) this.adopt(r.body);
    return r;
  }

  /** Drop the persisted device secret / refresh / in-memory access (after server delete, or to force a new guest). */
  clearLocal(): void {
    this.saved = null;
    this.access = null;
    try {
      this.o.storage?.setItem(KEY, '');
    } catch {
      /* ignore */
    }
    const sec = this.o.secretStorage;
    if (sec) {
      void (sec.deleteItem ? sec.deleteItem(KEY) : sec.setItem(KEY, '')).catch(() => undefined);
    }
  }

  /**
   * `DELETE /me` then wipe local secrets and bootstrap a fresh guest. Returns:
   *   ok        server erased (or already gone: 401) and a new guest is ready
   *   offline   could not reach the server (local state untouched — try again)
   *   error     server refused for another reason (local state untouched)
   */
  async deleteAccount(): Promise<'ok' | 'offline' | 'error'> {
    if (!(await this.ensure())) return 'offline';
    const token = this.currentToken();
    const r = await this.raw<{ ok?: boolean; deleted?: boolean; error?: string }>(
      'DELETE',
      '/me',
      undefined,
      token,
    );
    // 200 = erased; 401 = already gone / token invalidated mid-flight — both mean "no account left".
    if (r.status === 0) return 'offline';
    if (r.status !== 200 && r.status !== 401) return 'error';
    this.clearLocal();
    if (!(await this.ensure())) return 'offline';
    return 'ok';
  }

  /**
   * Sign out: best-effort `POST /auth/logout` (revokes this device's refresh tokens), then wipe local secrets and
   * bootstrap a fresh guest (the sign-in gate shows again). Always signs out locally. Returns:
   *   ok        server acknowledged (or the session was already gone)
   *   offline   the server could not be reached; local session still cleared
   */
  async logout(): Promise<'ok' | 'offline'> {
    let reached = false;
    if (await this.ensure().catch(() => false)) {
      const token = this.currentToken();
      if (token) {
        const r = await this.raw<{ ok?: boolean }>('POST', '/auth/logout', {}, token).catch(() => ({
          status: 0,
          body: null,
        }));
        reached = r.status !== 0;
      }
    }
    this.clearLocal();
    await this.ensure().catch(() => false);
    return reached ? 'ok' : 'offline';
  }

  private currentToken(): string | undefined {
    return this.access?.token;
  }

  // ── internals ──────────────────────────────────────────────────────────────────────────────────────────────

  private hydrate(): Promise<void> {
    if (this.hydrated || !this.o.secretStorage) {
      this.hydrated = true;
      return Promise.resolve();
    }
    if (!this.hydrateGate) {
      this.hydrateGate = (async () => {
        const sec = this.o.secretStorage!;
        let fromSecure = parsePersisted(await sec.getItem(KEY).catch(() => null));
        if (!fromSecure) {
          const plain = parsePersisted(this.o.storage?.getItem(KEY) ?? null);
          if (plain) {
            // Migrate existing guests out of plain MMKV into the Keychain without losing the wallet.
            fromSecure = plain;
            try {
              await sec.setItem(KEY, JSON.stringify(plain));
              this.o.storage?.setItem(KEY, '');
            } catch {
              /* keep using the in-memory copy; plain still has it if secure write failed */
            }
          }
        } else {
          // Secure won: scrub any leftover plain copy.
          try {
            this.o.storage?.setItem(KEY, '');
          } catch {
            /* ignore */
          }
        }
        this.saved = fromSecure;
        this.hydrated = true;
      })().catch(() => {
        this.hydrated = true;
      });
    }
    return this.hydrateGate;
  }

  private async bootstrap(): Promise<boolean> {
    await this.hydrate();
    const s = this.saved;
    if (s?.refreshToken) {
      const r = await this.raw('POST', '/auth/refresh', { refresh_token: s.refreshToken });
      if (r.status === 200 && this.adopt(r.body)) return true;
      if (r.status === 0 || r.status >= 500) return false; // offline / server trouble: try again later, keep state
    }
    if (s) {
      const r = await this.raw('POST', '/auth/device', {
        device_id: s.deviceId,
        device_secret: s.deviceSecret,
      });
      if (r.status === 200 && this.adopt(r.body)) return true;
      if (r.status !== 401) return false;
      // 401: this device is unknown/revoked server-side — only then start over with a new guest.
    }
    const r = await this.raw('POST', '/auth/guest', { platform: this.o.platform });
    return (r.status === 201 || r.status === 200) && this.adopt(r.body);
  }

  private adopt(body: unknown): boolean {
    if (!isTokenResponse(body)) return false;
    const prev = this.saved;
    const deviceSecret = body.device_secret ?? (prev?.deviceId === body.device_id ? prev.deviceSecret : null);
    if (!deviceSecret) return false;
    this.saved = {
      userId: body.user_id,
      deviceId: body.device_id,
      deviceSecret,
      refreshToken: body.refresh_token ?? null,
    };
    this.access = {
      token: body.access_token,
      exp: Number(body.access_expires_at) || this.now() + 10 * 60_000,
    };
    void this.persist();
    return true;
  }

  private async persist(): Promise<void> {
    const raw = JSON.stringify(this.saved);
    const sec = this.o.secretStorage;
    if (sec) {
      try {
        await sec.setItem(KEY, raw);
        try {
          this.o.storage?.setItem(KEY, '');
        } catch {
          /* ignore */
        }
        return;
      } catch {
        /* fall through to plain storage so we do not lose the session */
      }
    }
    try {
      this.o.storage?.setItem(KEY, raw);
    } catch {
      /* storage full / unavailable: the in-memory account still works this launch */
    }
  }

  private async raw<T>(
    method: string,
    path: string,
    body?: unknown,
    token?: string,
    timeoutOverrideMs?: number,
  ): Promise<ApiResult<T>> {
    const headers: Record<string, string> = { 'content-type': 'application/json' };
    if (token) headers.authorization = `Bearer ${token}`;
    const timeoutMs = timeoutOverrideMs ?? this.o.timeoutMs ?? 8000;
    const st = this.o.setTimeout ?? ((fn: () => void, ms: number) => setTimeout(fn, ms));
    try {
      const res = await Promise.race([
        this.o.fetch(this.o.baseUrl + path, {
          method,
          headers,
          ...(body === undefined ? {} : { body: JSON.stringify(body) }),
        }),
        new Promise<null>((resolve) => st(() => resolve(null), timeoutMs)),
      ]);
      if (!res) return { status: 0, body: null };
      const j = (await res.json().catch(() => null)) as T | null;
      return { status: res.status, body: j };
    } catch {
      return { status: 0, body: null };
    }
  }
}
